/* eslint-disable react/no-array-index-key */
import * as React from 'react';

import { NoDataTd, Sortable, Table, Tbody, Td, Th, Thead, Tr } from '@lightspeed/cirrus-table';
import { Bone } from '@lightspeed/flame/Bone';
import { Card } from '@lightspeed/flame/Card';
import { Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Text, TextLink } from '@lightspeed/flame/Text';
import Moment from 'moment';

import { toBoolean } from 'root/apps/pages/utils/sanitize';
import Pagination from 'root/apps/PurchaseOrder/ShipmentHistory/components/Pagination';
import { ErrorMessageContext } from 'root/apps/PurchaseOrder/ShipmentHistory/contexts/ErrorMessage';
import { useOrdering } from 'root/apps/PurchaseOrder/ShipmentHistory/hooks/use-ordering';
import { getApiError } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/api-error-codes';
import { formatCurrency, formatNumber } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/format';
import { toArray } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/to-array';

import ShipmentStatus from './ShipmentStatus';

import type { Page } from 'root/apps/PurchaseOrder/ShipmentHistory/tabs';
import type {
  APIV3OrderShipment,
  OrderShipment,
} from 'root/apps/PurchaseOrder/ShipmentHistory/types';

interface Props {
  isMultiCurrency: boolean;
  vendorCurrencyCode?: string;
  setPage: (page: Page) => void;
}

const PER_PAGE = 50;
const FIRST_PAGE = 1;

const Shipments: React.FC<Props> = ({ isMultiCurrency, vendorCurrencyCode, setPage }) => {
  const { setError } = React.useContext(ErrorMessageContext);
  const [data, setData] = React.useState<OrderShipment[]>();
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [pagination, setPagination] = React.useState<Pagination>({
    perPage: PER_PAGE,
    currentPage: FIRST_PAGE,
  });
  const [hasProductCostPermission] = React.useState<boolean | null>(
    toBoolean(window.merchantos.session.sessData?.Rights?.product_cost)
  );

  const { currency_code: currencyCode } = window.merchantos.locale;
  const tdWidth = isMultiCurrency ? '15%' : '25%';
  const { id: orderId } = window.history.getFilters();
  const systemCustomerId = window.merchantos.session.getSystemCustomerID();

  const { orderedBy, orderDirection, getOrderDirection, setOrderDirection, getOrderParamForAPIV3 } =
    useOrdering();

  const getShipments = async ({
    url,
    isPrevious,
    isNext,
    sort = '-orderShipmentID',
    currentPage,
  }: {
    url?: string;
    isPrevious?: boolean;
    isNext?: boolean;
    sort?: string;
    currentPage?: number;
  } = {}) => {
    setIsLoading(true);
    try {
      const res = await fetch(
        url
          ? url.replace('rad.localdev', 'localhost:3000')
          : `/API/V3/Account/${systemCustomerId}/Order/${orderId}/Shipment.json?limit=${PER_PAGE}${
              sort ? `&sort=${sort}` : ''
            }`
      );
      if (res.status !== 200) {
        const { title, message } = getApiError('GENERIC_GET');
        throw new Error(title || message);
      }

      const {
        '@attributes': { previous, next },
        OrderShipment: shipments,
      }: APIV3OrderShipment = await res.json();

      setPagination((prev) => {
        let newCurrentPage = currentPage || prev.currentPage;
        if (isPrevious) {
          newCurrentPage = prev.currentPage - 1;
        } else if (isNext) {
          newCurrentPage = prev.currentPage + 1;
        }

        return {
          ...prev,
          previous,
          next,
          currentPage: newCurrentPage,
          direction: null,
          handlePrevious: () => {
            if (previous) {
              setPagination((prev) => ({ ...prev, direction: 'previous' }));
              getShipments({ url: previous, isPrevious: true });
            }
          },
          handleNext: () => {
            if (next) {
              setPagination((prev) => ({ ...prev, direction: 'next' }));
              getShipments({ url: next, isNext: true });
            }
          },
        };
      });
      setData(toArray(shipments));
    } catch (err) {
      setError(getApiError('GENERIC_GET'));
    }
    setIsLoading(false);
    setIsInitialLoading(false);
  };

  const getShipmentsCount = async () => {
    try {
      const res = await fetch(
        `/API/V3/Account/${systemCustomerId}/Order/${orderId}/Shipment.json?count=1`
      );

      if (res.status !== 200) {
        const { title, message } = getApiError('GENERIC_GET');
        throw new Error(title || message);
      }

      const {
        '@attributes': { count },
      } = await res.json();

      setPagination((prev) => ({ ...prev, count: Number(count) }));
    } catch (err) {
      setError(getApiError('GENERIC_GET'));
    }

    setIsLoading(false);
  };

  React.useEffect(() => {
    getShipments();
    getShipmentsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!orderedBy) return;

    getShipments({ sort: getOrderParamForAPIV3(), currentPage: FIRST_PAGE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedBy, orderDirection]);

  return (
    <Card>
      <Pagination
        isLoading={isLoading}
        isInitialLoading={isInitialLoading}
        pagination={pagination}
      />
      <Divider />
      <Table>
        <Thead>
          <Tr>
            <Th width={tdWidth}>Shipment</Th>
            <Th width={tdWidth}>Status</Th>
            <Sortable.Th
              data-test="sort-orderShipmentID"
              disabled={!data?.length}
              width={tdWidth}
              direction={getOrderDirection('orderShipmentID')}
              onClick={() => setOrderDirection('orderShipmentID')}
            >
              Date received
            </Sortable.Th>
            <Sortable.Th
              data-test="sort-paymentDueDate"
              disabled={!data?.length}
              width={tdWidth}
              direction={getOrderDirection('paymentDueDate')}
              onClick={() => setOrderDirection('paymentDueDate')}
            >
              Invoice due date
            </Sortable.Th>
            <Th width={tdWidth} textAlign="end">
              Quantity
            </Th>
            {hasProductCostPermission ? (
              <>
                {isMultiCurrency ? (
                  <Th width={tdWidth} textAlign="end">
                    Total <var>({vendorCurrencyCode})</var>
                  </Th>
                ) : null}
                {isInitialLoading ? (
                  <Th width={tdWidth}>
                    <Bone height={3} />
                  </Th>
                ) : (
                  <Th width={tdWidth} textAlign="end">
                    Total {isMultiCurrency ? <var>({currencyCode})</var> : ''}
                  </Th>
                )}
              </>
            ) : null}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading
            ? new Array(3).fill(0).map((_v, index) => (
                <Tr key={index}>
                  {new Array(isMultiCurrency ? 5 : 4).fill(0).map((_x, i) => (
                    <Td key={i}>
                      <Bone height={3} width="100%" />
                    </Td>
                  ))}
                </Tr>
              ))
            : null}
          {!isInitialLoading && !data?.length ? (
            <Tr>
              <NoDataTd width="100%" colSpan={6} textAlign="center">
                <Flex paddingY={6} width="100%" justifyContent="center" flexDirection="column">
                  <img
                    style={{ width: '250px', alignSelf: 'center' }}
                    alt="empty table"
                    src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/empty-table.svg"
                  />
                  <Text size="normal" fontWeight="bold" color="black" paddingTop={4}>
                    No items have been added to inventory.
                  </Text>
                </Flex>
              </NoDataTd>
            </Tr>
          ) : null}

          {!isLoading && data?.length
            ? data?.map((shipment, index) => {
                const shipmentLink = `/?name=purchase.views.purchase&form_name=view&id=${shipment.orderID}&tab=shipment_history&shipment_id=${shipment.orderShipmentID}`;
                return (
                  <Tr key={shipment.orderShipmentID}>
                    <Td>
                      <TextLink
                        data-test={`shipment-${index}-link`}
                        fontWeight="bold"
                        href={shipmentLink}
                        onClick={(e) => {
                          e.preventDefault();
                          window.history.pushState(null, '', shipmentLink);
                          setPage('view');
                        }}
                      >
                        PO #<var>{shipment.orderID}</var>-<var>{shipment.sequenceNumber}</var>
                      </TextLink>
                      {shipment.shipmentPackingRefNum ? (
                        <Text
                          as="p"
                          data-test={`shipment-refnum-${index}-text`}
                          className="notranslate"
                        >
                          {shipment.shipmentPackingRefNum}
                        </Text>
                      ) : null}
                    </Td>
                    <Td>
                      <ShipmentStatus
                        status={shipment.status}
                        index={index}
                        data-test={`shipment-status-${index}-text`}
                      />
                    </Td>
                    <Td>
                      <Text
                        className="notranslate"
                        data-test={`shipment-receptiondate-${index}-text`}
                      >
                        {Moment(shipment.receptionDate)
                          .locale(window.merchantos.locale.lang)
                          .format('ll')}
                      </Text>
                    </Td>
                    <Td>
                      <Text
                        className="notranslate"
                        data-test={`shipment-paymentDueDate-${index}-text`}
                      >
                        {shipment.paymentDueDate
                          ? Moment(shipment.paymentDueDate)
                              .locale(window.merchantos.locale.lang)
                              .format('ll')
                          : '-'}
                      </Text>
                    </Td>
                    <Td textAlign="right" data-test={`shipment-totalqtyreceived-${index}-text`}>
                      <var>
                        {shipment?.totalQtyReceived ? formatNumber(shipment.totalQtyReceived) : '-'}
                      </var>
                    </Td>
                    {hasProductCostPermission ? (
                      <>
                        {isMultiCurrency ? (
                          <Td textAlign="right">
                            <var>
                              {shipment?.totalVendorCost
                                ? formatCurrency(
                                    shipment.totalVendorCost,
                                    shipment.vendorCurrencyCode
                                  )
                                : '-'}
                            </var>
                          </Td>
                        ) : null}
                        <Td textAlign="right" data-test={`shipment-totalcost-${index}-text`}>
                          <var>
                            {shipment.totalCost
                              ? formatCurrency(shipment.totalCost, shipment.currencyCode)
                              : '-'}
                          </var>
                        </Td>
                      </>
                    ) : null}
                  </Tr>
                );
              })
            : null}
        </Tbody>
      </Table>
      <Divider />
      <Pagination
        isLoading={isLoading}
        isInitialLoading={isInitialLoading}
        pagination={pagination}
      />
    </Card>
  );
};

export default Shipments;
